<template>
  <div>
    <PageLoading v-if="hasLoadingOverlay" :messages="loadingMessages" />

    <SurveyLayout v-if="isSurveyLayout" />
    <PreDashboard v-else-if="isPreDashboard" />
    <SignEditor v-else-if="isSignEditorMode" />
    <NewDashboard v-else-if="isNewDashboard" />
    <Dashboard v-else-if="isDashboardLayout" />
    <External v-else-if="isExternal" />
    <Default v-else />

    <EndOfSessionDialog
      :show="showLogoutDialog"
      @closeLogoutDialog="closeLogoutDialog"
    />

    <DialogHasProductAccess
      :show="showPermissionDialog"
      :selectedProduct="pageToCheckPermission"
      :status="permissionStatus"
      @close="closeAccessDialog"
    />
  </div>
</template>

<script>
import EndOfSessionDialog from "@/components/main/dashboard/EndOfSessionDialog";
import PageLoading from "@/components/PageLoading.vue";
import Dashboard from "@/layouts/Dashboard.vue";
import Default from "@/layouts/Default";
import External from "@/layouts/External.vue";
import NewDashboard from "@/layouts/NewDashboard.vue";
import PreDashboard from "@/layouts/PreDashboard.vue";
import SignEditor from "@/layouts/SignEditor";
import SurveyLayout from "@/layouts/SurveyLayout";
import styles from "@/styles/variables.scss";
import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
  name: "App",

  components: {
    Dashboard,
    Default,
    External,
    SignEditor,
    PageLoading,
    EndOfSessionDialog,
    NewDashboard,
    PreDashboard,
    SurveyLayout,
  },

  computed: {
    ...mapGetters([
      "isAuthenticated",
      "isAuthenticating",
      "isChangingSimulationMode",
      "loadingMessages",
      "showLoadingOverlay",
      "showLogoutDialog",
      "showPermissionDialog",
      "pageToCheckPermission",
      "permissionStatus",
      "isDashTester",
    ]),

    hasLoadingOverlay() {
      return (
        (!this.$route.name && this.$route.fullPath === "/") ||
        this.$route.meta.loading ||
        this.isAuthenticating ||
        this.isChangingSimulationMode ||
        this.showLoadingOverlay
      );
    },

    isSurveyLayout() {
      return (
        this.$route.meta.layout === "survey" || this.isChangingSimulationMode
      );
    },

    isPreDashboard() {
      return this.$route.meta.layout === "pre-dashboard";
    },

    isSignEditorMode() {
      return this.$route.meta.layout === "editor";
    },

    isNewDashboard() {
      return this.$route.meta.layout === "dashboard" && this.isDashTester;
    },

    isDashboardLayout() {
      return this.$route.meta.layout === "dashboard" && !this.isDashTester;
    },

    isExternal() {
      return this.$route.meta.layout === "external";
    },
  },

  methods: {
    ...mapMutations([
      "setShowLogoutDialog",
      "clearDelayedAccessDialog",
      "resetPermissionDialog",
      "setPermissionDialog",
    ]),

    ...mapActions(["logoutWithDialog"]),

    closeLogoutDialog() {
      this.setShowLogoutDialog(false);
    },

    closeAccessDialog() {
      this.clearDelayedAccessDialog();
      this.resetPermissionDialog();
    },

    setPermissionDialogByUrl({ data }) {
      this.setPermissionDialog({
        show: true,
        status: data.code,
        app: data.product || data.resource,
      });
    },
  },

  beforeMount() {
    this.$vuetify.theme.themes.light.primary = styles.primarySuite;
    this.$vuetify.theme.themes.light.accent = styles.accentSuite;
    this.$vuetify.theme.themes.dark.primary = styles.primarySuiteDark;
  },

  created() {
    this.$axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        let logoutMessages = [
          "EXPIRED_TOKEN",
          "INVALID_TOKEN",
          "MISSING_TOKEN",
          "COMPANY_BANNED",
        ];
        let noProductPermissionMessages = [
          "USER_IS_NOT_ADMIN",
          "PLAN_TRIAL_ENDED",
          "PRODUCT_IN_DISABLED_PLAN",
          "PRODUCT_DOES_NOT_EXIST_IN_PLANS",
          "USER_IS_NOT_ALLOWED_TO_ACCESS_THIS_RESOURCE",
        ];
        if (
          error.response?.status === 401 &&
          logoutMessages.includes(error.response.data?.code)
        ) {
          await this.logoutWithDialog();
        }
        if (
          error.response?.status === 403 &&
          noProductPermissionMessages.includes(error.response.data?.code)
        ) {
          this.setPermissionDialogByUrl(error.response);
        }
        return Promise.reject(error);
      }
    );
  },
};
</script>

<style>
html {
  overflow-y: auto !important;
}

.disable-scrollbars::-webkit-scrollbar {
  width: 0px;
  background: transparent; /* Chrome/Safari/Webkit */
}

.disable-scrollbars {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}
</style>
